import { useEffect, useMemo, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useUser, useIsAuthenticated, useHasHydrated } from '@/stores';
import { AccessDeniedPage } from '@/pages/errors/AccessDeniedPage';
import { usePermissions } from '../hooks/usePermissions';

interface ProtectedRouteProps {
  children: ReactNode;
  // Single permission check (ABAC format: module:resource:action:scope)
  requiredPermission?: string;
  // Multiple permissions (AND by default, OR if requireAny=true)
  requiredPermissions?: string[];
  // Module-level access check (any permission in module grants access)
  requiredModule?: string;
  // Role requirement (for strict role-based routes)
  requiredRole?: string | string[];
  // Use OR logic for multiple permissions
  requireAny?: boolean;
  // Rendered in place when authenticated user lacks required permission
  fallback?: ReactNode;
}

const normalizeRole = (r: string | undefined): string => {
  if (!r) return '';
  return r.toLowerCase().replace(/_/g, '');
};

export function ProtectedRoute({
  children,
  requiredPermission,
  requiredPermissions = [],
  requiredModule,
  requiredRole,
  requireAny = false,
  fallback,
}: ProtectedRouteProps) {
  const navigate = useNavigate();
  const user = useUser();
  const isAuthenticated = useIsAuthenticated();
  const isHydrated = useHasHydrated();
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasModuleAccess,
    hasRole,
    isSuperAdmin,
  } = usePermissions();
  const isUnauthenticated = isHydrated && (!isAuthenticated || !user);

  // Compute authorization status
  const isAuthorized = useMemo(() => {
    if (!isHydrated || !isAuthenticated || !user) {
      return null; // Still checking
    }

    // Role check (if required)
    if (requiredRole) {
      const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
      const hasRequiredRole = hasRole(requiredRole);

      // If ONLY superadmin is required (not in an OR list), deny non-superadmins
      const onlySuperAdminRequired = roles.length === 1 && normalizeRole(roles[0]) === 'superadmin';
      if (onlySuperAdminRequired && !isSuperAdmin) {
        return false;
      }

      // For multiple roles with requireAny, user needs at least one
      if (!hasRequiredRole) {
        return false;
      }
    }

    // Module access check
    if (requiredModule && !hasModuleAccess(requiredModule)) {
      return false;
    }

    // Single permission check
    if (requiredPermission && !hasPermission(requiredPermission)) {
      return false;
    }

    // Multiple permissions check
    if (requiredPermissions.length > 0) {
      const hasRequired = requireAny
        ? hasAnyPermission(requiredPermissions)
        : hasAllPermissions(requiredPermissions);
      if (!hasRequired) {
        return false;
      }
    }

    return true;
  }, [
    isHydrated,
    isAuthenticated,
    user,
    isSuperAdmin,
    requiredRole,
    requiredModule,
    requiredPermission,
    requiredPermissions,
    requireAny,
    hasRole,
    hasModuleAccess,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  ]);

  useEffect(() => {
    if (isUnauthenticated) {
      void navigate({ to: '/login' });
    }
  }, [isUnauthenticated, navigate]);

  // Show loading while checking
  if (!isHydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isUnauthenticated) {
    return null;
  }

  if (isAuthorized === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthorized) {
    return <>{fallback ?? <AccessDeniedPage />}</>;
  }

  return <>{children}</>;
}
